/**
 * Setting objects' local poses, and joining parts by their connector anchors
 * (`anchors.ts`), as one undo step.
 *
 * A pose goes where it is seen (`editRoute.ts`): the page that shows for a
 * page's shared content, else the state that shows, else Base.
 *
 * An object keeps its rotation mode: one in Euler mode (no
 * `rotationQuaternion`) stays in Euler mode, so whatever drives its Euler
 * `rotation` keeps working, and so does an entry that faults. Undo, and the
 * rollback of the entries before a fault, put the rotation back as a
 * quaternion, as every `TransformCommand` undo does.
 */

import { Matrix, Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { Node } from '@babylonjs/core/node';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';

import type { SceneObject } from '../../core/SceneObject';
import type { Vec3 } from '../../scenedata/common';
import { numericTuple, type TransformOverride } from '../../scenedata/behaviors/propertiesdata';
import { Layout } from '../../behaviors/layout';
import { LayoutItem } from '../../behaviors/layoutitem';
import { Properties } from '../../behaviors/properties';
import { TransformCommand, type TransformSnapshot } from '../undo/TransformCommand';
import { BehaviorCommitCommand } from '../undo/BehaviorCommitCommand';
import type { UndoCommand } from '../undo/UndoCommand';
import { dispatchBehaviorChanged } from '../undo/editor-events';
import { liveLocalPose, setStateTransform, transformEditTargetState, withoutOwnedFields } from '../states/statesActions';
import { capturePageState } from '../states/pageCapture';
import { pageStateRefusal, routeOf, type Route } from './editRoute';
import { matePose, objectAnchors, type Pose, type WorldAnchor } from './anchors';
import { keyframeRefusal, prepareBatch, refuse, sceneOf } from './checks';
import { editorRootsUnder, isEditorRoot, mountEditorRoot } from './documentRoot';
import { needsReferenceCopy } from './referenceEdit';
import { bundle, rollBack } from './steps';
import type { AuthoringPorts, BatchResult, Refusal } from './types';

/** One object's new local pose. Fields left out keep their value. */
export interface TransformEntry {
    object: SceneObject;
    /** In the parent's frame. */
    position?: Vec3;
    /** Euler degrees. */
    rotation?: Vec3;
    scale?: Vec3;
    /**
     * Join the object's `anchor` to `toAnchor` on `to`, after the fields
     * above: the object turns so the two face each other, spins `spin`
     * degrees about their shared axis (counter-clockwise looking down the
     * target anchor), then moves until the points meet.
     */
    mate?: { anchor: string; to: SceneObject; toAnchor: string; spin?: number };
}

const DEG = Math.PI / 180;
const EPSILON = 1e-9;
/** How close `setStateTransform` takes a position to be to the placement it echoes. */
const PLACEMENT_EPSILON = 1e-5;

/**
 * Set each entry's pose, in the order given, as one undo step. A later entry
 * sees what an earlier one did, and one that leaves the pose as it is records
 * nothing.
 *
 * The pose is written where it is seen, in this order:
 * - Shared content of a Pages container, while `ports.pageEditing` says page
 *   editing is on for it and one page shows: that page's state on the object
 *   (`capturePageState`), with the whole live pose.
 * - While a state other than Base shows on the object or its group: that
 *   state, with each component the entry changed or the state already keys
 *   (`setStateTransform`).
 * - Otherwise Base: authored data. An Absolute child a layout places by its
 *   `inset` has the move written into the inset too, which is what the layout
 *   places it from (LAYOUT_ENGINE §11); depth is not part of an inset.
 * In a page or state, a position a layout writes is left out of what is
 * stored, and a position moved back onto Base's placement drops the state's
 * key for it. Parent layouts are solved again afterwards.
 *
 * An object inside a placed reference copies the reference into the scene
 * first, in the same step (`orphaned`, as for `deleteObjects`), and its pose
 * means the frame it had in the reference. An entry that leaves the pose as
 * it is copies nothing, except one with a mate, whose pose is known only
 * after the copy.
 *
 * An editor root under a moved object stays in the scene frame: it is mounted
 * again in the same edit.
 *
 * Each entry is refused on its own when its object or mate target is deleted
 * or its copy cannot be made; when it changes the pose of the editor root
 * (`EDITOR_ROOT`: that pose is the scene frame); while a keyframe is selected
 * on it or an ancestor (`STATE_ROUTED`: the pose would land on Base, out of
 * sight); when it changes the pose of shared content showing the state its
 * page assigns it or an ancestor while `ports.pageEditing` is off
 * (`STATE_ROUTED`: that state is the page's, maybe other pages' too); when it
 * changes a position or scale a layout writes (`LAYOUT_OWNED`: the next solve
 * would undo it), except an inset child's position at Base; when, at Base, it
 * changes a pose component Base binds to a property (`BINDING_OWNED`: the
 * property writes it again whenever it changes); and when its mate names an
 * anchor an object lacks, or joins the object to itself or to a node under it
 * (`INVALID_MATE`). While the scene plays or an undo runs, every entry is
 * refused, and so is every entry when, while a copy uploads, play starts, an
 * object an entry needs is deleted, a keyframe is selected over an entry's
 * object, or its page's state starts showing on it.
 *
 * A vector that is not three finite numbers throws before anything changes.
 */
export async function setTransforms(
    entries: readonly TransformEntry[],
    ports: AuthoringPorts = {},
): Promise<BatchResult<SceneObject> & { orphaned: string[] }> {
    if (entries.length === 0) return { applied: [], refused: [], command: null, orphaned: [] };
    for (const entry of entries) assertNumbers(entry);
    sceneOf(entries.flatMap(entry => entry.mate ? [entry.object, entry.mate.to] : [entry.object]));

    // A copy may move an object to another parent, keeping its world pose:
    // the frame it had is what the asked-for pose means.
    const before = new Map<SceneObject, { parent: Node | null; parentWorld: Matrix }>();
    for (const { object } of entries) {
        if (before.has(object) || object.transform.isDisposed() || !needsReferenceCopy(object)) continue;
        const parent = object.transform.parent;
        before.set(object, { parent, parentWorld: parent ? parent.computeWorldMatrix(true).clone() : Matrix.Identity() });
    }
    const { ready, refused, steps, orphaned } = await prepareBatch(entries, ports, {
        check: entry => entryRefusal(entry, ports),
        // A keyframe can be selected, or a page's state start showing, while a
        // copy uploads. The other checks compare values with a frame the copy
        // may have changed.
        late: ({ object }) => keyframeRefusal(object, 'a pose') ?? pageStateRefusal(object, ports, transformEditTargetState, 'a pose'),
        changes,
        needs: ({ object, mate }) => mate ? [object, mate.to] : [object],
    });
    // From the frame an object had to the one the copy gave it, fixed before any
    // entry runs: an earlier entry may move the new parent, and the object moves
    // with it, as it would have under the old one.
    const offsets = new Map<SceneObject, Matrix>();
    for (const [object, was] of before) {
        const parent = object.transform.parent;
        if (object.transform.isDisposed() || parent === was.parent) continue;
        offsets.set(object, parent ? was.parentWorld.multiply(Matrix.Invert(parent.computeWorldMatrix(true))) : was.parentWorld);
    }

    const applied: SceneObject[] = [];
    const moved = new Set<SceneObject>();
    try {
        for (const { entry } of ready) {
            const { object, mate } = entry;
            const route = routeOf(object, ports, transformEditTargetState);
            const was = TransformCommand.capture(object);
            const offset = offsets.get(object);
            try {
                if (offset) applyInFrame(object.transform, entry, offset);
                else applyLocal(object.transform, entry);
                object.transform.computeWorldMatrix(true);
                if (mate) applyMate(object, mate);
                const after = TransformCommand.capture(object);
                const recorded = samePose(was, after) ? [] : await record(object, route, was, after);
                steps.push(...recorded);
                if (recorded.length > 0) moved.add(object);
            } catch (error) {
                // Not a step yet: put this entry's pose back before the steps are.
                // Nothing authored has changed yet: a Base entry folds its pose
                // into authored data last, after every step that can fault.
                showPose(object.transform, was);
                throw error;
            }
            applied.push(object);
        }
        steps.push(...remountUnder(moved));
    } catch (error) {
        await rollBack(steps, error);
        throw error;
    } finally {
        resolveParentLayouts(applied);
    }
    // Named for what moved; a mate that moved nothing still made a copy.
    const named = moved.size > 0 ? [...moved] : applied;
    const label = named.length === 1 ? `Transform ${named[0].name}` : `Transform ${named.length} objects`;
    return { applied, refused, command: steps.length > 0 ? bundle(label, steps) : null, orphaned };
}

/**
 * Put the editor roots under the moved objects back in the scene frame, as
 * steps of the same edit, so its undo and redo keep them there too.
 */
function remountUnder(moved: ReadonlySet<SceneObject>): UndoCommand[] {
    const steps: UndoCommand[] = [];
    const under = (object: SceneObject): boolean => {
        for (let n = object.getParent(); n; n = n.getParent()) if (moved.has(n)) return true;
        return false;
    };
    for (const root of [...moved].filter(object => !under(object)).flatMap(editorRootsUnder)) {
        const was = TransformCommand.capture(root);
        mountEditorRoot(root, root.getParent());
        const after = TransformCommand.capture(root);
        if (!samePose(was, after)) steps.push(new TransformCommand(root, was, after));
    }
    return steps;
}

// ── Checks ───────────────────────────────────────────────────────────────────

function assertNumbers(entry: TransformEntry): void {
    for (const field of ['position', 'rotation', 'scale'] as const) {
        const value: unknown = entry[field];
        if (value === undefined) continue;
        if (!Array.isArray(value) || value.length !== 3 || !value.every(n => typeof n === 'number' && Number.isFinite(n))) {
            throw new TypeError(`[setTransforms] ${field} of "${entry.object.name}" must be [x, y, z] with three finite numbers, got ${JSON.stringify(value)}`);
        }
    }
    const spin = entry.mate?.spin;
    if (spin !== undefined && !Number.isFinite(spin)) {
        throw new TypeError(`[setTransforms] mate spin of "${entry.object.name}" must be a finite number, got ${String(spin)}`);
    }
}

/**
 * Whether the entry would leave its object's pose other than it is now, by the
 * comparison {@link samePose} makes once it is written. A mate's pose is known
 * only once it runs.
 */
function changes(entry: TransformEntry): boolean {
    const changed = changedFields(entry);
    return changed.position || changed.rotation || changed.scale;
}

/** Which pose components the entry would change; a mate may change position and rotation. */
function changedFields({ object, position, rotation, scale, mate }: TransformEntry): Record<'position' | 'rotation' | 'scale', boolean> {
    const now = TransformCommand.capture(object);
    const turn = rotation && Quaternion.FromEulerAngles(rotation[0] * DEG, rotation[1] * DEG, rotation[2] * DEG).asArray();
    return {
        position: !!mate || (!!position && !near(position, now.position)),
        rotation: !!mate || (!!turn && !sameTurn(turn, now.rotation)),
        scale: !!scale && !near(scale, now.scale),
    };
}

function differs(value: Vec3 | undefined, current: Vector3): boolean {
    return value !== undefined && !near(value, current.asArray());
}

function samePose(a: TransformSnapshot, b: TransformSnapshot): boolean {
    return near(a.position, b.position) && sameTurn(a.rotation, b.rotation) && near(a.scale, b.scale);
}

/** Quaternions `q` and `-q` are one rotation. */
function sameTurn(a: readonly number[], b: readonly number[]): boolean {
    return near(a, b) || near(a, b.map(n => -n));
}

function near(a: readonly number[], b: readonly number[], epsilon = EPSILON): boolean {
    return a.length === b.length && a.every((n, i) => Math.abs(n - b[i]) <= epsilon);
}

function entryRefusal(entry: TransformEntry, ports: AuthoringPorts): Refusal | null {
    const { object, position, scale, mate } = entry;
    if (isEditorRoot(object) && changes(entry)) {
        return refuse(
            'EDITOR_ROOT',
            `"${object.name}" holds the editor's content in the scene frame and cannot be moved; move what is inside it instead`,
            object,
        );
    }
    const keyframe = keyframeRefusal(object, 'a pose');
    if (keyframe) return keyframe;
    // An entry that changes nothing records nothing, so it is not refused.
    const page = changes(entry) ? pageStateRefusal(object, ports, transformEditTargetState, 'a pose') : null;
    if (page) return page;
    const bound = boundRefusal(entry, ports);
    if (bound) return bound;
    const owned = object.getRuntimeOwnedTransformFields();
    if (owned.position && (mate !== undefined || differs(position, object.transform.position))) {
        const inset = insetItem(object);
        if (!inset) {
            return refuse(
                'LAYOUT_OWNED',
                `A layout places "${object.name}", so its next solve would undo a new position; rearrange it through the layout instead`,
                object,
            );
        }
        if (mate !== undefined || routeOf(object, ports, transformEditTargetState).to !== 'base') {
            return refuse(
                'LAYOUT_OWNED',
                `"${object.name}" is placed by its inset, which is Base's; move it at Base, where a move changes the inset`,
                object,
            );
        }
    }
    if (owned.scale && differs(scale, object.transform.scaling)) {
        return refuse(
            'LAYOUT_OWNED',
            `A layout sizes "${object.name}", so its next solve would undo a new scale; resize it through the layout instead`,
            object,
        );
    }
    return mate ? mateRefusal(object, mate) : null;
}

/**
 * A component of the pose that Base binds to a property (Properties
 * `transform`) is written again whenever that property changes, so a move of
 * it at Base would be undone out of sight. A mate counts as moving position
 * and rotation, since its pose is known only once it runs.
 */
function boundRefusal(entry: TransformEntry, ports: AuthoringPorts): Refusal | null {
    const { object } = entry;
    const base = object.getBehavior(Properties)?.getData().transform;
    if (!base || routeOf(object, ports, transformEditTargetState).to !== 'base') return null;
    const moved = changedFields(entry);
    const field = (['position', 'rotation', 'scale'] as const).find(f => moved[f] && base[f] && !numericTuple(base[f]));
    if (!field) return null;
    return refuse(
        'BINDING_OWNED',
        `"${object.name}" has its ${field} bound to a property (${JSON.stringify(base[field])}), which writes it again whenever the property changes; move what is inside it, or change the binding`,
        object,
    );
}

/** Why `mate` can't join `object`, in words that name the anchors there are. */
function mateRefusal(object: SceneObject, mate: NonNullable<TransformEntry['mate']>): Refusal | null {
    const target = mate.to;
    if (target.transform.isDisposed()) return refuse('DISPOSED', `"${target.name}" has been deleted`, target);
    if (target === object) return refuse('INVALID_MATE', 'mate joins two different objects.', object);
    for (let up = target.getParent(); up; up = up.getParent()) {
        if (up === object) {
            return refuse(
                'INVALID_MATE',
                `mate target "${target.name}" is inside "${object.name}", so its anchor would move with it. Join it to an object outside the one being moved.`,
                object,
            );
        }
    }
    for (const [owner, name] of [[object, mate.anchor], [target, mate.toAnchor]] as const) {
        const anchors = objectAnchors(owner);
        if (anchors.some(anchor => anchor.name === name)) continue;
        const listed = anchors.length
            ? `Its anchors: ${anchors.slice(0, 24).map(anchor => anchor.name).join(', ')}${anchors.length > 24 ? ', …' : ''}.`
            : 'It has none.';
        return refuse('INVALID_MATE', `"${owner.name}" has no anchor "${name}". ${listed}`, owner);
    }
    return null;
}

// ── Recording ────────────────────────────────────────────────────────────────

/** Record the move from `was` to `after`, already on the node, where `route` says it is seen. */
async function record(object: SceneObject, route: Route, was: TransformSnapshot, after: TransformSnapshot): Promise<UndoCommand[]> {
    if (route.to === 'page') {
        const pose = withoutOwnedFields(object, liveLocalPose(object));
        return [await capturePageState(route.page, `Key ${object.name} on ${route.page.pageName}`, state => ({ ...state, transform: pose }))];
    }
    if (route.to === 'state') {
        const keyed = object.getBehavior(Properties)?.getData().states?.[route.state]?.transform;
        const pose = stateTransform(object, keyed, was, after);
        const { command } = await setStateTransform(object, pose, { record: false });
        if (command) return [command];
        const unkeyed = await unkeyPlacement(object, route.state, pose);
        if (unkeyed) return [unkeyed];
        throw new Error(`[setTransforms] the state "${route.state}" of "${object.name}" stored nothing for the move`);
    }
    const steps: UndoCommand[] = [new TransformCommand(object, was, after)];
    const inset = await moveInset(object, was, after);
    if (inset) steps.push(inset);
    // Base: fold the pose into authored data, past any baseline Properties
    // holds, as the command's undo and redo do.
    const props = object.getBehavior(Properties);
    props?.resetSavedTransform();
    object.captureTransform();
    if (props) dispatchBehaviorChanged({ objectPath: object.getPath(), behaviorType: 'Properties', action: 'updated' });
    return steps;
}

/**
 * The state's transform after a move from `was` to `after`: each component the
 * move changed or the state already keys, as it is now. A state's transform is
 * a whole local pose, and a component it leaves out shows Base's.
 */
function stateTransform(object: SceneObject, keyed: TransformOverride | undefined, was: TransformSnapshot, after: TransformSnapshot): TransformOverride {
    const pose: TransformOverride = {};
    if (keyed?.position !== undefined || !near(was.position, after.position)) pose.position = [...after.position] as Vec3;
    if (keyed?.rotation !== undefined || !sameTurn(was.rotation, after.rotation)) pose.rotation = [...after.rotation] as [number, number, number, number];
    if (keyed?.scale !== undefined || !near(was.scale, after.scale)) pose.scale = [...after.scale] as Vec3;
    return withoutOwnedFields(object, pose);
}

/**
 * A position moved back onto Base's placement is not keyed by an object that
 * owns the state itself (`setStateTransform` stores nothing for it). When the
 * state still keys an old position, drop that key, so the object shows Base's
 * position as asked. The step, or null when that is not the case.
 */
async function unkeyPlacement(object: SceneObject, state: string, pose: TransformOverride): Promise<UndoCommand | null> {
    const props = object.getBehavior(Properties);
    const before = props?.getData();
    const definition = before?.states?.[state];
    // Where Base places it: the baseline Properties holds while a state shows, else authored data.
    const saved = props?.getAuthoredTransform()?.position;
    const placement = saved ? [saved.x, saved.y, saved.z] : object.getSceneObjectData().transform?.translation ?? [0, 0, 0];
    if (!props || !before || definition?.transform?.position === undefined || !pose.position || Object.keys(pose).length !== 1
        || !near(numbers(pose.position), placement, PLACEMENT_EPSILON)) {
        return null;
    }
    const { position: _old, ...rest } = definition.transform;
    const { transform: _transform, ...plain } = definition;
    const states = { ...before.states, [state]: Object.keys(rest).length > 0 ? { ...plain, transform: rest } : plain };
    const errors = await props.commit({ states });
    if (errors.length > 0) {
        throw new Error(`[setTransforms] the state "${state}" of "${object.name}" refused the move: ${errors.map(error => error.message).join('; ')}`);
    }
    dispatchBehaviorChanged({ objectPath: object.getPath(), behaviorType: 'Properties', action: 'updated' });
    return new BehaviorCommitCommand(object, 'Properties', before, props.getData());
}

/** The numbers of a pose component; a bound one has none. */
function numbers(component: readonly unknown[]): number[] {
    return component.every(n => typeof n === 'number') ? component as number[] : [];
}

/** The behavior an Absolute child placed by its `inset` keeps it in (its own Layout, else its LayoutItem), or null. */
function insetItem(object: SceneObject): Layout | LayoutItem | null {
    if (!object.getParent()?.getBehavior(Layout)) return null;
    const item = object.getBehavior(Layout) ?? object.getBehavior(LayoutItem);
    const data = item?.getData();
    return item && data?.positionMode === 'Absolute' && data.inset != null ? item : null;
}

/**
 * Write a move of an inset child into its inset, the same distance with Y
 * flipped (the inset is measured down from the top), never below 0. The step,
 * or null for a child with no inset, a move with no X or Y, or one that
 * resizes it too: a resize moves the centre about the corner the inset holds.
 */
async function moveInset(object: SceneObject, was: TransformSnapshot, after: TransformSnapshot): Promise<UndoCommand | null> {
    const item = insetItem(object);
    const before = item?.getData();
    if (!item || !before?.inset || !near(was.scale, after.scale)) return null;
    const dx = after.position[0] - was.position[0];
    const dy = after.position[1] - was.position[1];
    const inset = { ...before.inset };
    if (Math.abs(dx) > EPSILON) inset.left = Math.max(0, (inset.left ?? 0) + dx);
    if (Math.abs(dy) > EPSILON) inset.top = Math.max(0, (inset.top ?? 0) - dy);
    if (inset.left === before.inset.left && inset.top === before.inset.top) return null;
    const errors = await item.commit({ inset });
    if (errors.length > 0) {
        throw new Error(`[setTransforms] the inset of "${object.name}" refused the move: ${errors.map(error => error.message).join('; ')}`);
    }
    const type = item instanceof Layout ? Layout.behaviorType : LayoutItem.behaviorType;
    return new BehaviorCommitCommand(object, type, before, item.getData());
}

/** Solve each moved object's parent layout again, so a rotation or scale re-flows its siblings now. */
function resolveParentLayouts(objects: readonly SceneObject[]): void {
    const parents = new Set(objects.filter(object => !object.transform.isDisposed()).map(object => object.getParent()));
    for (const parent of parents) parent?.getBehavior(Layout)?.invalidate();
}

// ── Applying ─────────────────────────────────────────────────────────────────

function applyLocal(node: TransformNode, { position, rotation, scale }: TransformEntry): void {
    if (position) node.position = new Vector3(...position);
    if (rotation) {
        const [x, y, z] = rotation.map(degrees => degrees * DEG);
        if (node.rotationQuaternion) node.rotationQuaternion = Quaternion.FromEulerAngles(x, y, z);
        else node.rotation = new Vector3(x, y, z);
    }
    if (scale) node.scaling = new Vector3(...scale);
}

/**
 * Apply the entry's pose as meant in the frame the node had, to the one it has
 * now; `offset` takes a local pose from the first to the second. The fields
 * the entry leaves out keep their value in the frame it had.
 */
function applyInFrame(node: TransformNode, entry: TransformEntry, offset: Matrix): void {
    if (!entry.position && !entry.rotation && !entry.scale) return;
    const was = decompose(
        Matrix.Compose(node.scaling, node.rotationQuaternion ?? Quaternion.FromEulerVector(node.rotation), node.position)
            .multiply(Matrix.Invert(offset)),
    );
    const { position, rotation, scale } = entry;
    const here = decompose(Matrix.Compose(
        scale ? Vector3.FromArray(scale) : was.scaling,
        rotation ? Quaternion.FromEulerAngles(rotation[0] * DEG, rotation[1] * DEG, rotation[2] * DEG) : was.rotation,
        position ? Vector3.FromArray(position) : was.position,
    ).multiply(offset));
    node.position = here.position;
    node.scaling = here.scaling;
    writeRotation(node, here.rotation);
}

function decompose(matrix: Matrix): { position: Vector3; rotation: Quaternion; scaling: Vector3 } {
    const parts = { position: new Vector3(), rotation: new Quaternion(), scaling: new Vector3() };
    matrix.decompose(parts.scaling, parts.rotation, parts.position);
    return parts;
}

function applyMate(object: SceneObject, mate: NonNullable<TransformEntry['mate']>): void {
    const own = anchorNamed(object, mate.anchor);
    const joined = anchorNamed(mate.to, mate.toAnchor);
    const node = object.transform;
    node.computeWorldMatrix(true);
    const part: Pose = { position: node.getAbsolutePosition().clone(), rotation: node.absoluteRotationQuaternion.clone() };
    setWorldPose(node, matePose(part, own, joined, mate.spin ?? 0));
}

function anchorNamed(object: SceneObject, name: string): WorldAnchor {
    const anchor = objectAnchors(object).find(candidate => candidate.name === name);
    if (!anchor) throw new Error(`[setTransforms] "${object.name}" lost its anchor "${name}" while the batch ran`);
    return anchor;
}

/** Put a node at a world pose, keeping its scale and rotation mode, under whatever parent it has. */
function setWorldPose(node: TransformNode, pose: Pose): void {
    const parent = node.parent as TransformNode | null;
    if (parent) {
        parent.computeWorldMatrix(true);
        writeRotation(node, Quaternion.Inverse(parent.absoluteRotationQuaternion).multiply(pose.rotation).normalize());
    } else {
        writeRotation(node, pose.rotation.clone());
    }
    node.setAbsolutePosition(pose.position);
    node.computeWorldMatrix(true);
}

/** Put `node` back at `pose`, in its rotation mode, without touching authored data. */
function showPose(node: TransformNode, pose: TransformSnapshot): void {
    node.position = Vector3.FromArray(pose.position);
    writeRotation(node, Quaternion.FromArray(pose.rotation));
    node.scaling = Vector3.FromArray(pose.scale);
    node.computeWorldMatrix(true);
}

/** Give `node` the local rotation `rotation` in the mode it is in. */
function writeRotation(node: TransformNode, rotation: Quaternion): void {
    if (node.rotationQuaternion) node.rotationQuaternion = rotation;
    else node.rotation = rotation.toEulerAngles();
}

